import { UserDto } from "../services/userManagementApi";
import { CountryCode } from "../services/deviceApi";
import { parsePhoneNumberFromString, isValidPhoneNumber, AsYouType } from "libphonenumber-js/min";

// True when running inside the SmartEco React Native WebView app (native Wi-Fi
// bridge available). False in a desktop/mobile browser, where the web
// onboarding flow is used instead.
export const isRNApp = (): boolean =>
  typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;

// True when the page is served over HTTPS. An HTTPS page CANNOT fetch the
// device's http://192.168.4.1 endpoint (browser mixed-content block), so the
// web onboarding flow falls back to opening the device's own captive-portal
// page (a top-level navigation, which browsers allow) instead of a direct POST.
export const isSecurePage = (): boolean =>
  typeof window !== 'undefined' && window.location.protocol === 'https:';

// Convert any stored mobile number ("+91 8585854574" or "+918585854574") into
// E.164 (no spaces) for the phone-input component. Returns '' on failure.
export const toE164 = (stored: string | null | undefined): string => {
  if (!stored) return '';
  const cleaned = stored.replace(/\s+/g, '');
  const parsed = parsePhoneNumberFromString(cleaned);
  return parsed && parsed.isValid() ? parsed.number : cleaned;
};

// Convert E.164 ("+918585854574") into the legacy backend format
// ("+91 8585854574") expected by the .NET API. Returns '' for empty input.
export const e164ToBackendFormat = (e164: string | null | undefined): string => {
  if (!e164) return '';
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed) return e164;
  return `+${parsed.countryCallingCode} ${parsed.nationalNumber}`;
};

// True if `value` is a valid international phone number in E.164 form.
export const isValidE164 = (value: string | null | undefined): boolean => {
  if (!value) return false;
  try {
    return isValidPhoneNumber(value);
  } catch {
    return false;
  }
};

// True if the input only contains a country dial code with no national digits
// (the phone-input component returns "+971" before the user types anything).
// Note: parsePhoneNumberFromString returns undefined for a single national
// digit (e.g. "+913") because it's too short to parse, which would wrongly
// report a partially typed number as empty and suppress the "valid number"
// error. AsYouType extracts the national digits even from incomplete input,
// so a single digit is correctly treated as non-empty.
export const isPhoneEmpty = (value: string | null | undefined): boolean => {
  if (!value) return true;
  const formatter = new AsYouType();
  formatter.input(value);
  return !formatter.getNumber()?.nationalNumber;
};

//AQI standard id
const userPreferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
const aqiStandardId = userPreferences.aqiStandardId || 1;

export const countryCodes: CountryCode[] = [
  { "country": "India", "code": "+91", "iso": "IN" },
  { "country": "United States", "code": "+1", "iso": "US" },
  { "country": "United Kingdom", "code": "+44", "iso": "GB" },
  { "country": "Australia", "code": "+61", "iso": "AU" },
  { "country": "Canada", "code": "+1", "iso": "CA" },
  { "country": "Singapore", "code": "+65", "iso": "SG" },
  { "country": "United Arab Emirates", "code": "+971", "iso": "UAE" },
  { "country": "Germany", "code": "+49", "iso": "DE" },
  { "country": "France", "code": "+33", "iso": "FR" },
  { "country": "Japan", "code": "+81", "iso": "JP" },
  { "country": "South Korea", "code": "+82", "iso": "KR" },
  { "country": "China", "code": "+86", "iso": "CN" },
  { "country": "Brazil", "code": "+55", "iso": "BR" },
  { "country": "South Africa", "code": "+27", "iso": "ZA" },
  { "country": "Mexico", "code": "+52", "iso": "MX" },
  { "country": "Italy", "code": "+39", "iso": "IT" },
  { "country": "Spain", "code": "+34", "iso": "ES" },
  { "country": "Netherlands", "code": "+31", "iso": "NL" },
  { "country": "Switzerland", "code": "+41", "iso": "CH" },
  { "country": "Sweden", "code": "+46", "iso": "SE" },
  { "country": "Norway", "code": "+47", "iso": "NO" },
  { "country": "Denmark", "code": "+45", "iso": "DK" },
  { "country": "Finland", "code": "+358", "iso": "FI" },
  { "country": "Russia", "code": "+7", "iso": "RU" },
  { "country": "Turkey", "code": "+90", "iso": "TR" },
  { "country": "Saudi Arabia", "code": "+966", "iso": "SA" },
  { "country": "Malaysia", "code": "+60", "iso": "MY" },
  { "country": "Thailand", "code": "+66", "iso": "TH" },
  { "country": "Indonesia", "code": "+62", "iso": "ID" },
  { "country": "Philippines", "code": "+63", "iso": "PH" },
  { "country": "Vietnam", "code": "+84", "iso": "VN" },
  { "country": "New Zealand", "code": "+64", "iso": "NZ" }
];

const STATUS_COLORS: any = {
  "Good": "#A0ED9F",
  "Moderate": "#FFE48A",
  "Unhealthy for Sensitive Groups": "#FFB36E",
  "Unhealthy": "#FF8880",
  "Very Unhealthy": "#D5A1FE",
  "Hazardous": "#CB89C4"
};

const parameterRangesForIndia: any = {
  "Good": [
    { "PM2.5": "0.0-30.0" },
    { "PM10": "0.0-50.0" },
    { "CO": "0.0-1.0" },
    { "O₃": "0.0-50.0" },
    { "NO₂": "0.0-40.0" },
    { "SO₂": "0.0-15.27" },
    { "CH₂O": "0.0-37.0" },
    { "CO₂": "0.0-600" },
    { "VOC": "0.0-1.0" },
    { "AQI": "0.0-50.0" },
    { "Mold": "0.0-2.0" },
    { "Virus": "0.0-2.0" },
    { "Temperature": "0-50"},
    { "Humidity": "0-50"}

  ],
  "Moderate": [
    { "PM2.5": "30.0-60.0" },
    { "PM10": "50.0-100.0" },
    { "CO": "1.0-2.0" },
    { "O₃": "50.0-100.0" },
    { "NO₂": "40.0-80.0" },
    { "SO₂": "15.65-30.53" },
    { "CH₂O": "37.0-100.0" },
    { "CO₂": "600-1000" },
    { "VOC": "1.0-2.0" },
    { "AQI": "50.0-100.0" },
    { "Mold": "2.1-5.0" },
    { "Virus": "2.1-5.0" },
    { "Temperature": "51-100"},
    { "Humidity": "51-100"}
  ],
  "Unhealthy for Sensitive Groups": [
    { "PM2.5": "60.0-90.0" },
    { "PM10": "100.0-250.0" },
    { "CO": "2.0-10.0" },
    { "O₃": "100.0-168.0" },
    { "NO₂": "80.0-180.0" },
    { "SO₂": "30.92-145.05" },
    { "CH₂O": "100.0-123.0" },
    { "CO₂": "1000-1500" },
    { "VOC": "2.0-3.0" },
    { "AQI": "100.0-150.0" },
    { "Mold": "5.1-8.0" },
    { "Virus": "5.1-8.0" }
  ],
  "Unhealthy": [
    { "PM2.5": "90.0-120.0" },
    { "PM10": "250.0-350.0" },
    { "CO": "10.0-17.0" },
    { "O₃": "168.0-208.0" },
    { "NO₂": "180.0-280.0" },
    { "SO₂": "145.43-305.36" },
    { "CH₂O": "123.0-370.0" },
    { "CO₂": "1500-2000" },
    { "VOC": "3.0-4.0" },
    { "AQI": "150.0-200.0" },
    { "Mold": "8.1-10.0" },
    { "Virus": "8.1-10.0" },
    { "Temperature": "101-200"},
    { "Humidity": "101-200"}    
  ],
  "Very Unhealthy": [
    { "PM2.5": "120.0-250.0" },
    { "PM10": "350.0-430.0" },
    { "CO": "17.0-34.0" },
    { "O₃": "208.0-748.0" },
    { "NO₂": "280.0-400.0" },
    { "SO₂": "305.74-610.72" },
    { "CH₂O": "370.0-615.0" },
    { "CO₂": "2000-5000" },
    { "VOC": "4.0-5.0" },
    { "AQI": "200.0-300.0" }
  ],
  "Hazardous": [
    { "PM2.5": "250.0+" },
    { "PM10": "430.0+" },
    { "CO": "34.0+" },
    { "O₃": "748.0+" },
    { "NO₂": "400.0+" },
    { "SO₂": "611.0+" },
    { "CH₂O": "615.0+" },
    { "CO₂": "5000+" },
    { "VOC": "5.0+" },
    { "AQI": "300.0+" }
  ]
};

const parameterRangesForUAE: any = {
  "Good": [
    { "PM2.5": "0.0-50.0" },
    { "PM10": "0.0-75.0" },
    { "CO": "0.0-5.0" },
    { "O₃": "0.0-109.3" },
    { "NO₂": "0.0-99.6" },
    { "SO₂": "0.0-35.12" },
    { "CH₂O": "0.0-37.0" },
    { "CO₂": "0.0-600" },
    { "VOC": "0.0-1.0" },
    { "AQI": "0.0-50.0" },
    { "Mold": "0.0-2.0" },
    { "Virus": "0.0-2.0" },
    { "Temperature": "0-50"},
    { "Humidity": "0-50"}
  ],
  "Moderate": [
    { "PM2.5": "50.0-60.0" },
    { "PM10": "75.0-150.0" },
    { "CO": "5.0-10.0" },
    { "O₃": "109.3-130.8" },
    { "NO₂": "99.6-400.4" },
    { "SO₂": "35.50-133.60" },
    { "CH₂O": "37.0-100.0" },
    { "CO₂": "600-1000" },
    { "VOC": "1.0-2.0" },
    { "AQI": "50.0-100.0" },
    { "Mold": "2.1-5.0" },
    { "Virus": "2.1-5.0" },
    { "Temperature": "51-100"},
    { "Humidity": "51-100"}
  ],
  "Unhealthy for Sensitive Groups": [
    { "PM2.5": "60.0-75.0" },
    { "PM10": "150.0-250.0" },
    { "CO": "10.0-14.3" },
    { "O₃": "130.8-182.2" },
    { "NO₂": "400.4-676.8" },
    { "SO₂": "134.00-185.12" },
    { "CH₂O": "100.0-123.0" },
    { "CO₂": "1000-1500" },
    { "VOC": "2.0-3.0" },
    { "AQI": "100.0-150.0" },
    { "Mold": "5.1-8.0" },
    { "Virus": "5.1-8.0" }
  ],
  "Unhealthy": [
    { "PM2.5": "75.0-150.0" },
    { "PM10": "250.0-350.0" },
    { "CO": "14.3-17.8" },
    { "O₃": "182.2-225.1" },
    { "NO₂": "676.8-1222.0" },
    { "SO₂": "185.50-304.29" },
    { "CH₂O": "123.0-370.0" },
    { "CO₂": "1500-2000" },
    { "VOC": "3.0-4.0" },
    { "AQI": "150.0-200.0" },
    { "Mold": "8.1-10.0" },
    { "Virus": "8.1-10.0" },
    { "Temperature": "101-200"},
    { "Humidity": "101-200"}
  ],
  "Very Unhealthy": [
    { "PM2.5": "150.0-250.0" },
    { "PM10": "350.0-420.0" },
    { "CO": "17.8-35.0" },
    { "O₃": "225.1-428.8" },
    { "NO₂": "1222.0-2350.0" },
    { "SO₂": "304.67-604.03" },
    { "CH₂O": "100.0-615.0" },
    { "CO₂": "1000-5000" },
    { "VOC": "4.0-5.0" },
    { "AQI": "200.0-300.0" }
  ],
  "Hazardous": [
    { "PM2.5": "250.0-500.0" },
    { "PM10": "420.0-600.0" },
    { "CO": "35.0-58.0" },
    { "O₃": "428.8-866.2" },
    { "NO₂": "2350.0+" },
    { "SO₂": "604.41+" },
    { "AQI": "300.0-500.0" },
    { "CH₂O": "615.0+" },
    { "CO₂": "5000+" },
    { "VOC": "5.0+" }
  ]
};

const parameterRangesForUSA: any = {
  "Good": [
    { "PM2.5": "0.0-9.0" },
    { "PM10": "0.0-54.0" },
    { "CO": "0.0-4.4" },
    { "O₃": "0.0-0.054" },
    { "NO₂": "0.0-53.0" },
    { "SO₂": "0.0-35.0" },
    { "CH₂O": "0.0-30.1" },
    { "CO₂": "0.0-600" },
    { "VOC": "0.0-1.0" },
    { "AQI": "0.0-50.0" },
    { "Mold": "0.0-2.0" },
    { "Virus": "0.0-2.0" },
    { "Temperature": "0-50"},
    { "Humidity": "0-50"}
  ],
  "Moderate": [
    { "PM2.5": "9.0-35.4" },
    { "PM10": "54.0-154.0" },
    { "CO": "4.4-9.4" },
    { "O₃": "0.054-0.07" },
    { "NO₂": "53.0-100.0" },
    { "SO₂": "36.0-75.0" },
    { "CH₂O": "30.1-81.4" },
    { "CO₂": "600-1000" },
    { "VOC": "1.0-2.0" },
    { "AQI": "50.0-100.0" },
    { "Mold": "2.1-5.0" },
    { "Virus": "2.1-5.0" },
    { "Temperature": "51-100"},
    { "Humidity": "51-100"}
  ],
  "Unhealthy for Sensitive Groups": [
    { "PM2.5": "35.4-55.4" },
    { "PM10": "154.0-254.0" },
    { "CO": "9.4-12.4" },
    { "O₃": "0.07-0.085" },
    { "NO₂": "100.0-360.0" },
    { "SO₂": "76.0-185.0" },
    { "CH₂O": "81.4-100.1" },
    { "CO₂": "1000-1500" },
    { "VOC": "2.0-3.0" },
    { "AQI": "100.0-150.0" },
    { "Mold": "5.1-8.0" },
    { "Virus": "5.1-8.0" }
  ],
  "Unhealthy": [
    { "PM2.5": "55.4-125.4" },
    { "PM10": "254.0-354.0" },
    { "CO": "12.4-15.4" },
    { "O₃": "0.085-0.105" },
    { "NO₂": "360.0-649.0" },
    { "SO₂": "186.0-304.0" },
    { "CH₂O": "100.1-301.2" },
    { "CO₂": "1500-2000" },
    { "VOC": "3.0-4.0" },
    { "AQI": "150.0-200.0" },
    { "Mold": "8.1-10.0" },
    { "Virus": "8.1-10.0" },
    { "Temperature": "101-200"},
    { "Humidity": "101-200"}
  ],
  "Very Unhealthy": [
    { "PM2.5": "125.4-225.4" },
    { "PM10": "354.0-424.0" },
    { "CO": "15.4-30.4" },
    { "O₃": "0.105-0.2" },
    { "NO₂": "649.0-1249.0" },
    { "SO₂": "305.0-604.0" },
    { "CH₂O": "301.2-500.6" },
    { "CO₂": "2000-5000" },
    { "VOC": "4.0-5.0" },
    { "AQI": "200.0-300.0" }
  ],
  "Hazardous": [
    { "PM2.5": "225.4+" },
    { "PM10": "424.0+" },
    { "CO": "30.4+" },
    { "O₃": "0.2+" },
    { "NO₂": "1249.0+" },
    { "SO₂": "604.0+" },
    { "CH₂O": "500.6+" },
    { "CO₂": "5000+" },
    { "AQI": "300.0+" },
    { "VOC": "5.0+" }
  ]
};

class HelperFunctions {

  // Building ranges map
  static BuildRangeMap(ranges: any) {

    const map: any = {};

    for (const status in ranges) {

      ranges[status].forEach((param: any) => {

        const name = Object.keys(param)[0];
        const range = String(param[name]);

        let min: number;
        let max: number;

        // Open-ended buckets like "225.4+" or "5000+" have no upper bound.
        // split("-") leaves parts[1] undefined and max becomes NaN, so the
        // Hazardous tier silently never matches — handle this form explicitly.
        if (!range.includes("-") && range.endsWith("+")) {
          min = parseFloat(range);
          max = Infinity;
        } else {
          const parts = range.split("-");
          min = parseFloat(parts[0]);
          max = parts[1]?.includes("+") ? Infinity : parseFloat(parts[1]);
        }

        if (!map[name]) map[name] = [];

        map[name].push({
          status,
          min,
          max
        });
      });
    }

    return map;
  }

  static countryRanges: any = {
    1: this.BuildRangeMap(parameterRangesForUSA),
    2: this.BuildRangeMap(parameterRangesForUAE),
    3: this.BuildRangeMap(parameterRangesForIndia)
  };

  //Function for getting last updated
  static GetLastUpdated(lastUpdated: string) {
    if (!lastUpdated) return "Unknown";

    const now = Date.now();
    const past = new Date(lastUpdated).getTime();
    if (isNaN(past)) return "Invalid date";

    const diffMs = now - past;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) return `${diffSeconds} seconds ago`;
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;

    // fallback to date if it's older
    const date = new Date(lastUpdated);
    return `${date.toLocaleString('en-US', options)}`;
  }

  //Funtion fo getting AQI background color for device details page
  static GetAQIBackgroundColor(value?: number): string {
    if (value === undefined || value === null || isNaN(value)) return '#F1F1F1'; // No data
    if (value <= 50) return '#EFFFEF'; // Good
    if (value <= 100) return '#FFFBEF'; // Moderate
    if (value <= 150) return '#FFF8F2'; // Unhealthy for Sensitive Groups
    if (value <= 200) return '#FFECEB'; // Unhealthy
    if (value <= 300) return '#F9F1FF'; // Very Unhealthy
    if (value <= 500) return '#FFF3FE'; // Hazardous
    return '#F1F1F1'; // Hazardous
  }

  //Function for getting Parameter color
  // static GetParameterColor(value?: number, parameterName?: string | null): string {
  //   if (value === undefined || value === null || isNaN(value)) return "#d6d4d0"; // No data
  //   if (parameterName === 'AQI') {
  //     if (value <= 50) return "#A0ED9F"; // Good
  //     if (value <= 100) return "#FFE48A"; // Moderate
  //     if (value <= 150) return "#FFB36E"; // Unhealthy for Sensitive Groups
  //     if (value <= 200) return "#FF8880"; // Unhealthy
  //     if (value <= 300) return "#D5A1FE"; // Very Unhealthy
  //     if (value <= 500) return "#CB89C4"; // Hazardous
  //     return "#B53264"; //Very Hazardous
  //   }
  //   if (parameterName === 'PM2.5') {
  //     if (value <= 9.0) return "#A0ED9F"; // Good
  //     if (value <= 35.4) return "#FFE48A"; // Moderate
  //     if (value <= 55.4) return "#FFB36E"; // Unhealthy for Sensitive Groups
  //     if (value <= 125.4) return "#FF8880"; // Unhealthy
  //     if (value <= 225.4) return "#D5A1FE"; // Very Unhealthy
  //     if (value <= 500) return "#CB89C4"; // Hazardous
  //     return "#B53264"; //Very Hazardous
  //   }
  //   if (parameterName === 'PM10') {
  //     if (value <= 54) return "#A0ED9F"; // Good
  //     if (value <= 154) return "#FFE48A"; // Moderate
  //     if (value <= 254) return "#FFB36E"; // Unhealthy for Sensitive Groups
  //     if (value <= 354) return "#FF8880"; // Unhealthy
  //     if (value <= 424) return "#D5A1FE"; // Very Unhealthy
  //     if (value <= 600) return "#CB89C4"; // Hazardous
  //     return "#B53264"; //Very Hazardous
  //   }
  //   if (parameterName === 'O₃') {
  //     if (value <= 0.054) return "#A0ED9F"; // Good
  //     if (value <= 0.07) return "#FFE48A"; // Moderate  
  //     if (value <= 0.085) return "#FFB36E"; // Unhealthy for Sensitive Groups
  //     if (value <= 0.105) return "#FF8880"; // Unhealthy
  //     if (value <= 0.2) return "#D5A1FE"; // Very Unhealthy
  //     if (value <= 1.0) return "#CB89C4"; // Hazardous
  //     return "#B53264"; //Very Hazardous
  //   }
  //   if (parameterName === 'CO') {
  //     if (value <= 4.4) return "#A0ED9F"; // Good
  //     if (value <= 9.4) return "#FFE48A"; // Moderate
  //     if (value <= 12.4) return "#FFB36E"; // Unhealthy for Sensitive Groups
  //     if (value <= 15.4) return "#FF8880"; // Unhealthy
  //     if (value <= 30.4) return "#D5A1FE"; // Very Unhealthy
  //     if (value <= 51.0) return "#CB89C4"; // Hazardous
  //     return "#B53264"; //Very Hazardous
  //   }
  //   if (parameterName === 'NO₂') {
  //     if (value <= 53) return "#A0ED9F"; // Good
  //     if (value <= 100) return "#FFE48A"; // Moderate
  //     if (value <= 360) return "#FFB36E"; // Unhealthy for Sensitive Groups
  //     if (value <= 649) return "#FF8880"; // Unhealthy
  //     if (value <= 1249) return "#D5A1FE"; // Very Unhealthy
  //     if (value <= 3000) return "#CB89C4"; // Hazardous
  //     return "#B53264"; //Very Hazardous
  //   }
  //   if (parameterName === 'Mold' || parameterName === 'Virus') {
  //     if (value <= 2) return "#A0ED9F"; // Good
  //     if (value <= 5) return "#FFE48A"; // Moderate
  //     if (value <= 8) return "#FFB36E"; // Unhealthy for Sensitive Groups
  //     if (value <= 10) return "#FF8880"; // Unhealthy
  //     return "#B53264"; //Very Hazardous
  //   }
  //   if (value <= 50) return "#A0ED9F"; // Good
  //   if (value <= 100) return "#FFE48A"; // Moderate
  //   if (value <= 200) return "#FFB36E"; // Unhealthy for Sensitive Groups
  //   if (value <= 300) return "#FF8880"; // Unhealthy
  //   if (value <= 400) return "#D5A1FE"; // Very Unhealthy
  //   if (value <= 500) return "#CB89C4"; // Hazardous
  //   return "#B53264"; //Very Hazardous
  // }

  static GetParameterColor(value?: number, parameterName?: string): string {

    if (value === undefined || value === null || isNaN(value)) return "#d6d4d0";

    const status = this.GetStatusText(value, parameterName);

    return STATUS_COLORS[status] || "#d6d4d0";
  }

  //Function for getting AQI text
  // static GetStatusText(value?: number, parameterName?: string | null): string {
  //   if (value === undefined || value === null) return "NA";
  //   if (parameterName === 'AQI') {
  //     if (value <= 50) return "Good"; // Good
  //     if (value <= 100) return "Moderate"; // Moderate
  //     if (value <= 150) return "Unhealthy for Sensitive Groups"; // Unhealthy for Sensitive Groups
  //     if (value <= 200) return "Unhealthy"; // Unhealthy
  //     if (value <= 300) return "Very Unhealthy"; // Very Unhealthy
  //     if (value <= 500) return "Hazardous"; // Hazardous
  //     return "Very Hazardous"; // Very Hazardous
  //   }
  //   if (parameterName === 'PM2.5') {
  //     if (value <= 9.0) return "Good"; // Good
  //     if (value <= 35.4) return "Moderate"; // Moderate
  //     if (value <= 55.4) return "Unhealthy for Sensitive Groups"; // Unhealthy for Sensitive Groups
  //     if (value <= 125.4) return "Unhealthy"; // Unhealthy
  //     if (value <= 225.4) return "Very Unhealthy"; // Very Unhealthy
  //     if (value <= 500) return "Hazardous"; // Hazardous
  //     return "Very Hazardous"; // Very Hazardous
  //   }
  //   if (parameterName === 'PM10') {
  //     if (value <= 54) return "Good"; // Good
  //     if (value <= 154) return "Moderate"; // Moderate
  //     if (value <= 254) return "Unhealthy for Sensitive Groups"; // Unhealthy for Sensitive Groups
  //     if (value <= 354) return "Unhealthy"; // Unhealthy
  //     if (value <= 424) return "Very Unhealthy"; // Very Unhealthy
  //     if (value <= 600) return "Hazardous"; // Hazardous
  //     return "Very Hazardous"; // Very Hazardous
  //   }
  //   if (parameterName === 'O₃') {
  //     if (value <= 0.054) return "Good"; // Good
  //     if (value <= 0.07) return "Moderate"; // Moderate
  //     if (value <= 0.085) return "Unhealthy for Sensitive Groups"; // Unhealthy for Sensitive Groups
  //     if (value <= 0.105) return "Unhealthy"; // Unhealthy
  //     if (value <= 0.2) return "Very Unhealthy"; // Very Unhealthy
  //     if (value <= 1.0) return "Hazardous"; // Hazardous
  //     return "Very Hazardous"; // Very Hazardous
  //   }
  //   if (parameterName === 'CO') {
  //     if (value <= 4.4) return "Good"; // Good
  //     if (value <= 9.4) return "Moderate"; // Moderate
  //     if (value <= 12.4) return "Unhealthy for Sensitive Groups"; // Unhealthy for Sensitive Groups
  //     if (value <= 15.4) return "Unhealthy"; // Unhealthy
  //     if (value <= 30.4) return "Very Unhealthy"; // Very Unhealthy
  //     if (value <= 51.0) return "Hazardous"; // Hazardous
  //     return "Very Hazardous"; // Very Hazardous
  //   }
  //   if (parameterName === 'NO₂') {
  //     if (value <= 53) return "Good"; // Good
  //     if (value <= 100) return "Moderate"; // Moderate
  //     if (value <= 360) return "Unhealthy for Sensitive Groups"; // Unhealthy for Sensitive Groups
  //     if (value <= 649) return "Unhealthy"; // Unhealthy
  //     if (value <= 1249) return "Very Unhealthy"; // Very Unhealthy
  //     if (value <= 3000) return "Hazardous"; // Hazardous
  //     return "Very Hazardous"; // Very Hazardous
  //   }
  //   if (parameterName === 'Mold' || parameterName === "Virus") {
  //     if (value <= 2) return "Very Low Risk"; // Good
  //     if (value <= 5) return "Moderate Risk"; // Moderate
  //     if (value <= 8) return "High Risk"; // Unhealthy for Sensitive Groups
  //     if (value <= 10) return "Severe Risk"; // Unhealthy
  //   }
  //   if (value <= 50) return "Good"; // Good
  //   if (value <= 100) return "Moderate"; // Moderate
  //   if (value <= 200) return "Unhealthy for Sensitive Groups"; // Unhealthy for Sensitive Groups
  //   if (value <= 300) return "Unhealthy"; // Unhealthy
  //   if (value <= 400) return "Very Unhealthy"; // Very Unhealthy
  //   if (value <= 500) return "Hazardous"; // Hazardous
  //   return "Very Hazardous"; // Very Hazardous
  // }

  static GetStatusText(value?: number, parameterName?: string): string {

    if (value === undefined || value === null) return "NA";

    const ranges = this.countryRanges[aqiStandardId]?.[parameterName!];

    if (!ranges) return "NA";

    for (const r of ranges) {
      if (value >= r.min && value <= r.max) {
        return r.status;
      }
    }

    return "NA";
  }

  // Function to get main pollutant, value, and unit in a single method
  static getMainPollutantAndValue(parameters: Record<number, any>, columns: any[]): { pollutant: string; value: string; unit: string } {
    if (!parameters || Object.keys(parameters).length === 0) {
      return { pollutant: "No Data", value: "", unit: "" };
    }

    let primaryDriverID: number | null = null;
    let maxSubIndex = -Infinity;

    for (const [driverID, param] of Object.entries(parameters)) {
      if (param && param.subIndex !== null && param.subIndex > maxSubIndex) {
        maxSubIndex = param.subIndex;
        primaryDriverID = Number(driverID);
      }
    }

    if (primaryDriverID === null) {
      return { pollutant: "No Data", value: "", unit: "" };
    }

    const mainParam = parameters[primaryDriverID];
    const pollutant = mainParam?.parameterName || "No Data";
    const value = mainParam?.value;

    // Find the unit from columns using the primaryDriverID
    const mainColumn = columns.find((col: any) => col.driverID === primaryDriverID);
    const unit = mainColumn?.unit || "";

    const formattedValue = value != null && !isNaN(Number(value))
      ? `${Math.round(Number(value) * 100) / 100}`
      : "";

    return { pollutant, value: formattedValue, unit };
  }

  static GetMaximumValueForParameter(parameterName: string) {
    if (parameterName === 'AQI') return 500;
    if (parameterName === 'PM2.5') return 500;
    if (parameterName === 'PM10') return 600;
    if (parameterName === 'O₃') return 1;
    if (parameterName === 'CO') return 51;
    if (parameterName === 'NO₂') return 3000;
    if (parameterName === 'CO₂') return 5000;
    if (parameterName === 'CH₂O') return 501;
    if (parameterName === 'VOC') return 3;
    if (parameterName === 'Temperature') return 100;
    if (parameterName === 'Humidity') return 100;
    if (parameterName === 'Mold') return 5;
    if (parameterName === 'Virus') return 5
    return 200;
  }

}


export const ExportDataTypes = [
  {
    label: 'PDF',
    value: 'pdf',
  },
  {
    label: 'Excel',
    value: 'excel',
  },
  {
    label: 'CSV',
    value: 'csv',
  },
  {
    label: 'Image',
    value: 'png',
  },
]

export const timeRanges = [
  { value: '12h', label: 'Last 12 Hours' },
  { value: '24h', label: 'Last 24 Hours' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom Range' },
];

export const options: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

export const EnvironmentModes = [
  {
    id: 1,
    mode: 'Kids',
  },
  {
    id: 2,
    mode: 'Elderly/ Sensitive group',
  },
  {
    id: 3,
    mode: 'Work Space',
  },
  {
    id: 4,
    mode: 'Kitchen'
  },
  {
    id: 5,
    mode: 'Hall'
  }
]

export const roleMap: Record<string, UserDto['role'][]> = {
  "Super Admin": ['Admin', 'Viewer'],
  "Admin": ['Viewer'],
};

  export const hasUserAccess = (role: string | undefined) => {
    if (role == "Super Admin" || role == "Admin") return true;

    return false;
  }


// ── Chatbot Q&A ───────────────────────────────────────────────────────────

export interface ChatbotQA {
  keywords: string[];
  answer: string;
}

export const CHATBOT_QA: ChatbotQA[] = [
  {
    keywords: ['hello', 'hi', 'hey', 'good morning', 'good afternoon', 'greetings'],
    answer: 'Hi! Welcome to SmartEco Support. How can I help you today?',
  },
  {
    keywords: ['air quality', 'aqi', 'pollution', 'pollutant', 'pm2.5', 'pm10'],
    answer: 'Air Quality Index (AQI) is a measure of how clean or polluted the air is. SmartEco monitors PM2.5, PM10, NO₂, SO₂, CO, and O₃ in real time across all your devices.',
  },
  {
    keywords: ['device', 'sensor', 'connect', 'offline', 'not working', 'disconnected'],
    answer: 'If your device is offline or not connecting, try restarting it and ensuring it has a stable network connection. You can also check the device status in the Devices section of the dashboard.',
  },
  {
    keywords: ['dashboard', 'loading', 'blank', 'slow', 'not loading'],
    answer: 'If the dashboard is not loading, please clear your browser cache and try again. Supported browsers are Chrome, Firefox, Safari, and Edge (latest two versions).',
  },
  {
    keywords: ['password', 'reset', 'forgot', 'login', 'access'],
    answer: 'To reset your password, go to the login page and click "Forgot Password". You will receive an email with reset instructions within a few minutes.',
  },
  {
    keywords: ['export', 'download', 'report', 'csv', 'excel'],
    answer: 'You can export data from any report page by clicking the Export button. CSV and Excel formats are supported for custom date ranges.',
  },
  {
    keywords: ['alert', 'notification', 'threshold', 'trigger'],
    answer: 'Alerts can be configured in the Alerts section. You can set thresholds for specific pollutants and choose email recipients to notify when limits are exceeded.',
  },
  {
    keywords: ['user', 'team', 'invite', 'role', 'permission', 'member'],
    answer: 'To invite team members, go to User Management > Add New User and enter their email and role. You can manage or revoke access at any time.',
  },
  {
    keywords: ['station', 'monitoring station', 'abu dhabi', 'location'],
    answer: 'Abu Dhabi operates over 20 air quality monitoring stations managed by EAD, placed across urban, industrial, and residential areas.',
  },
  {
    keywords: ['support', 'contact', 'help', 'email', 'call', 'phone'],
    answer: 'You can reach our support team at support@smarteco.ae, call us at +971 2 000 0000, or use WhatsApp at +971 50 000 0000. We are available Sun–Thu, 9:00 AM – 6:00 PM GST.',
  },
  {
    keywords: ['ticket', 'raise', 'issue', 'problem', 'complaint'],
    answer: 'You can raise a support ticket by clicking the "Raise Ticket" button at the top right of the Help & Support page. Our team will respond within 2–3 business hours.',
  },
  {
    keywords: ['thank', 'thanks', 'bye', 'goodbye'],
    answer: 'You are welcome! If you need any further assistance, feel free to ask. Have a great day!',
  },
];

export const getBotReply = (input: string): string => {
  const lower = input.toLowerCase();
  const match = CHATBOT_QA.find((qa) =>
    qa.keywords.some((kw) => lower.includes(kw))
  );
  return match?.answer ?? "I'm sorry, I didn't understand that. Could you rephrase? You can also contact our support team at support@smarteco.ae.";
};

/**
 * Derive a human-readable device status label from backend fields.
 * Handles the INITIALIZE state set during first-time onboarding.
 */
export type DeviceStatusLabel = 'Active' | 'Initializing' | 'Inactive';
export type DeviceStatusVariant = 'active' | 'initializing' | 'inactive';

export const getDeviceStatusLabel = (
  isActive: boolean,
  operationalStatus?: string | null,
): { label: DeviceStatusLabel; variant: DeviceStatusVariant } => {
  const status = (operationalStatus ?? '').toUpperCase();
  if (status === 'INITIALIZE') {
    return { label: 'Initializing', variant: 'initializing' };
  }
  return isActive
    ? { label: 'Active', variant: 'active' }
    : { label: 'Inactive', variant: 'inactive' };
};

export default HelperFunctions;
